import React, {
  RefObject,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  type ModelPosition,
  type ModelWriter,
  type ModelNode,
  type ModelElement,
} from 'ckeditor5';
import last from 'lodash/last';
import isNil from 'lodash/isNil';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import {
  MicIcon,
} from 'core/icons';
import {
  ToolbarItemProps,
} from 'views/EMR/components/TextAreaToolbar/index';
import {
  SpeechRecognitionContext,
} from 'pages/Dashboard/contexts/speechRecognition';
import Spinner from 'pages/Dashboard/components/Spinner';
import EditorStateCommand from 'views/EMR/components/ckeditor-plugins/EditorState/command';

const PUNCTUATION_PATTERN = /\s([.|,|;|:|?|!])/ig;

function normalizePunctuation(text: string) {
  return text.replace(PUNCTUATION_PATTERN, (match, char) => char);
}

function cleanupFromTemporaries(elements: ModelNode[], writer: ModelWriter) {
  elements.forEach((element) => {
    const children = Array.from((element as ModelElement).getChildren() ?? []) as ModelElement[];
    children.forEach((child) => {
      if (child.name === 'temporary') {
        writer.remove(child);
      }
    });
  });
}

export default function DictationToolbarItem({ editor }: ToolbarItemProps) {
  const {
    listening,
    loading,
    startVoiceRecognition,
    stopVoiceRecognition,
    text,
    unprocessedText,
  } = useContext(SpeechRecognitionContext);

  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const toggleVoiceRecognition = () => {
    if (listening) {
      stopVoiceRecognition();
    } else {
      if (!editor?.ui.focusTracker.isFocused) {
        const editorStateCommand = editor?.commands.get('editorState') as EditorStateCommand;
        const hasEverBeenFocused = editorStateCommand?.hasEverBeenFocused ?? true;

        const { end } = editor?.model.document.selection.getLastRange() ?? {};
        editor?.model.change((writer) => {
          const lastElement = last(Array.from(
            editor?.model?.document?.getRoot?.('content')?.getChildren() ?? [],
          ));
          // Only move to end if user has NEVER focused the editor
          if (!hasEverBeenFocused && !isNil(lastElement) && end?.isAtStart) {
            const newPosition = editor?.model.createPositionAt(lastElement, 'end');
            writer.setSelection(newPosition);
          }
        });
      }
      editor?.editing.view.focus();
      startVoiceRecognition();
    }
  };

  function scrollToCursor() {
    editor?.editing.view.scrollToTheSelection();
  }

  useEffect(() => {
    editor?.keystrokes?.set?.('Alt+T', (event, cancel: () => void) => {
      toggleButtonRef.current?.click?.();
      cancel();
    });
  }, [editor?.ui.isReady]);

  useEffect(() => {
    editor?.model?.change((writer) => {
      const { end } = editor?.model.document.selection.getLastRange() ?? {};
      if (end?.isAtStart && !isNil(end.nodeAfter)) {
        const newPosition = editor?.model.createPositionAfter(end.nodeAfter);
        writer.setSelection(newPosition);
      }

      const parts = text.split('\n');
      parts.forEach((partText, index) => {
        const { selection } = editor?.model?.document ?? {};
        const currentPosition = selection?.getLastPosition() as ModelPosition;

        writer.insertText(normalizePunctuation(
          `${partText === '' || currentPosition.path[1] <= 1 ? '' : ' '}${partText}`,
        ), currentPosition);

        if (parts.length - 1 !== index) {
          const positionAfterText = editor?.model?.document?.selection
            ?.getLastPosition() as ModelPosition;
          const currentElement = positionAfterText.parent;

          writer.split(positionAfterText);

          const newParagraph = currentElement.nextSibling;

          if (newParagraph) {
            const newPosition = editor?.model.createPositionAt(newParagraph, 0);
            writer.setSelection(newPosition);
          }
        }
      });
      scrollToCursor();
    });
  }, [text]);

  useEffect(() => {
    editor?.model?.enqueueChange({ isUndoable: false }, (writer) => {
      if (!listening) {
        cleanupFromTemporaries(
          Array.from(editor?.model.document.getRoot('content')?.getChildren() ?? []),
          writer,
        );
      }
    });
  }, [listening]);

  useEffect(() => {
    editor?.model?.enqueueChange({ isUndoable: false }, (writer) => {
      const elements = Array.from(editor?.model.document.getRoot('content')?.getChildren() ?? []);
      cleanupFromTemporaries(elements ?? [], writer);

      const { selection } = editor?.model?.document ?? {};
      const currentPosition = selection?.getLastPosition() as ModelPosition;

      try {
        writer.insertElement('temporary', { value: unprocessedText }, currentPosition);
      } catch (error) {
        console.error('failed to insert temporary element', currentPosition);
      }
      scrollToCursor();
    });
  }, [unprocessedText]);

  return (
    <ToolTip title={listening ? 'Stop Dictation' : 'Start Dictation'}>
      <div className={clsx({ 'cursor-not-allowed': loading })}>
        <IconButton
          ref={toggleButtonRef as RefObject<HTMLButtonElement>}
          className={clsx('nw-dictation-button', { '!text-red-500': listening })}
          onClick={toggleVoiceRecognition}
          disabled={loading}
        >
          {loading ? <Spinner className="!w-4 !h-4" /> : <MicIcon className="!w-4 !h-4 !text-inherit" />}
        </IconButton>
      </div>
    </ToolTip>
  );
}
